import { clamp, lerp, phase, smooth } from "./mathUtils";
import type { KeyState, VaultDoorState } from "./VaultDoor";

// =============================================================================
// computeRoundState — pure-function port of vbComputeRoundState from
// RoundSequencer.jsx (lines 41-237). Drives the full 20-second cinematic round
// timeline given a single time `t` (in seconds) and outcome.
//
// Phase windows (verbatim from source comment block):
//   0.0 – 1.5    intro / idle reveal
//   1.5 – 4.0    key 1 flies in, slots, turns          (mult 1.00 → 1.50)
//   4.0 – 6.0    key 2                                 (mult 1.50 → 2.10)
//   6.0 – 8.5    key 3 — vibration starts              (mult 2.10 → 3.40)
//   8.5 – 11.0   key 4 — fog deepens, dial speeds up   (mult 3.40 → 5.20)
//   11.0– 13.0   key 5 ARMING — 5th hole pulses red, smoke wisps
//   13.0– 15.5   key 5 strikes
//                  → outcome === 'win'  : freeze 13.5, cashout flash → door opens TOWARD
//                  → outcome === 'bust' : 5th key turns at 14.0, color desat → door opens AWAY
//   15.5 – 20.0  resolution + payout + reset
// =============================================================================

interface KeyTiming {
  arriveStart: number;
  arriveEnd: number;
  turnStart: number;
  turnEnd: number;
  multAfter: number;
  dialNotch: number;
}

// Source: RoundSequencer.jsx:19-25 (VB_KEY_TIMES tuple).
const VB_KEY_TIMES: readonly KeyTiming[] = [
  { arriveStart: 1.6, arriveEnd: 2.6, turnStart: 2.6, turnEnd: 3.0, multAfter: 1.5, dialNotch: 6 },
  { arriveStart: 4.0, arriveEnd: 4.7, turnStart: 4.7, turnEnd: 5.0, multAfter: 2.1, dialNotch: 12 },
  { arriveStart: 6.0, arriveEnd: 6.7, turnStart: 6.7, turnEnd: 7.0, multAfter: 3.4, dialNotch: 18 },
  { arriveStart: 8.5, arriveEnd: 9.2, turnStart: 9.2, turnEnd: 9.5, multAfter: 5.2, dialNotch: 24 },
] as const;

// Source: RoundSequencer.jsx:26-32 (VB_KEY5).
const VB_KEY5 = {
  armingFrom: 11.0,
  arriveStart: 13.0,
  arriveEnd: 13.7,
  turnStart: 13.7,
  turnEnd: 14.0,
} as const;

export const VB_DEFAULT_CASHOUT_T = 8.6;
export const VB_ROUND_LENGTH = 20.0;

// Source: RoundSequencer.jsx:50-59 — multiplier anchor table.
const ANCHORS: readonly [number, number][] = [
  [0.0, 1.0],
  [1.6, 1.0],
  [3.0, 1.5],
  [5.0, 2.1],
  [7.0, 3.4],
  [9.5, 5.2],
  [13.0, 7.2],
  [14.0, 11.5],
] as const;

// Smooth lerp through ANCHORS at time tt. Source: RoundSequencer.jsx:60-71.
function multiplierAt(tt: number): number {
  for (let i = 1; i < ANCHORS.length; i++) {
    const cur = ANCHORS[i]!;
    const prev = ANCHORS[i - 1]!;
    if (tt <= cur[0]) {
      return lerp(prev[1], cur[1], smooth(phase(tt, [prev[0], cur[0]])));
    }
  }
  return ANCHORS[ANCHORS.length - 1]![1];
}

export interface KeyFlightInfo {
  idx: number;
  progress: number;
  originAngle: number;
}

export interface RoundState extends VaultDoorState {
  multiplier: number;
  cashoutMultiplier: number;
  cashedOut: boolean;
  /** 0..1 green flash on cashout (win path). */
  cashoutFlash: number;
  /** 0..1 red flash on bust 5th-key turn. */
  bustFlash: number;
  /** Brief b/w wash on bust. */
  desaturate: boolean;
  /** "win"/"bust" once final result is locked, else null. */
  showResult: "win" | "bust" | null;
  /** Camera shake offset in px. */
  shake: { x: number; y: number };
  /** Live key-flight info for any keys mid-flight. */
  keyFlights: KeyFlightInfo[];
  /** 0..1 ambient danger ramp (red pulse intensity). */
  danger: number;
  /** Brief slow-mo flag while cashout flash is active. */
  timeFreeze: boolean;
  /** "win" or "bust" — propagated for downstream logic. */
  outcome: "win" | "bust";
  /** Cashout target time in seconds. */
  cashoutT: number;
}

export function computeRoundState(
  t: number,
  outcome: "win" | "bust",
  cashoutT: number = VB_DEFAULT_CASHOUT_T,
): RoundState {
  // ----- Multiplier (smooth interp through keypoints) -----
  let multiplier = multiplierAt(t);

  // ----- Decide if we cashed out (WIN) or hit 5th key (BUST) -----
  const cashedOut = outcome === "win" && t >= cashoutT;
  const cashoutMultiplier = multiplierAt(cashoutT);
  if (cashedOut) multiplier = cashoutMultiplier;

  // ----- Per-key state -----
  const keyStates: KeyState[] = ["idle", "idle", "idle", "idle", "idle"];
  const keyAngles: number[] = [0, 0, 0, 0, 0];
  const hasKeys: boolean[] = [false, false, false, false, false];
  const keySmokes: number[] = [0, 0, 0, 0, 0];
  const keyFlights: KeyFlightInfo[] = [];

  // First 4 keys: standard pattern. Source: RoundSequencer.jsx:102-126.
  for (let i = 0; i < 4; i++) {
    const k = VB_KEY_TIMES[i]!;
    const arriveP = phase(t, [k.arriveStart, k.arriveEnd]);
    const turnP = phase(t, [k.turnStart, k.turnEnd]);

    if (t < k.arriveStart) {
      keyStates[i] = "idle";
    } else if (t < k.arriveEnd) {
      // Key in flight.
      keyFlights.push({
        idx: i,
        progress: arriveP * 0.85,
        originAngle: -Math.PI / 4 - i * 0.4 + (i % 2 ? Math.PI / 1.6 : 0),
      });
      keyStates[i] = "idle";
    } else if (t < k.turnEnd) {
      hasKeys[i] = true;
      keyAngles[i] = turnP;
      keyStates[i] = "locked";
    } else {
      hasKeys[i] = true;
      keyAngles[i] = 1;
      keyStates[i] = "locked";
    }
  }

  // 5th key — arming smoke from t=11. Source: RoundSequencer.jsx:128-164.
  const armingP = phase(t, [VB_KEY5.armingFrom, VB_KEY5.arriveStart]);
  if (t >= VB_KEY5.armingFrom && t < VB_KEY5.arriveStart) {
    keyStates[4] = "arming";
    keySmokes[4] = armingP;
  } else if (t >= VB_KEY5.arriveStart && !cashedOut) {
    if (outcome === "bust") {
      // BUST path: 5th key actually arrives + turns.
      const arriveP5 = phase(t, [VB_KEY5.arriveStart, VB_KEY5.arriveEnd]);
      const turnP5 = phase(t, [VB_KEY5.turnStart, VB_KEY5.turnEnd]);
      if (t < VB_KEY5.arriveEnd) {
        keyFlights.push({
          idx: 4,
          progress: arriveP5 * 0.85,
          originAngle: Math.PI / 1.5,
        });
        keyStates[4] = "arming";
        keySmokes[4] = 1;
      } else if (t < VB_KEY5.turnEnd) {
        hasKeys[4] = true;
        keyAngles[4] = turnP5;
        keyStates[4] = "arming";
        keySmokes[4] = 1 - turnP5 * 0.5;
      } else {
        hasKeys[4] = true;
        keyAngles[4] = 1;
        keyStates[4] = "flared";
      }
    } else {
      // WIN path: 5th never arrives, smoke fades after cashout.
      keyStates[4] = "arming";
      keySmokes[4] = clamp(1 - phase(t, [cashoutT, cashoutT + 1.5]), 0, 1);
    }
  } else if (cashedOut) {
    keyStates[4] = "idle";
    keySmokes[4] = clamp(1 - phase(t, [cashoutT, cashoutT + 1.5]), 0, 1);
  }

  // After cashout (win), ALL locked keyholes flare gold.
  // Source: RoundSequencer.jsx:166-169.
  if (cashedOut && t > cashoutT && t < cashoutT + 1.5) {
    for (let i = 0; i < 4; i++) keyStates[i] = "flared";
  }

  // ----- Tension / danger ramps -----
  // Source: RoundSequencer.jsx:171-178.
  let tension = 0;
  if (t >= 6) tension = phase(t, [6, 11]) * 0.7;
  if (t >= 11) tension = 0.7 + phase(t, [11, 14]) * 0.3;
  if (cashedOut) tension = clamp(tension - phase(t, [cashoutT, cashoutT + 2]) * 0.7, 0, 1);

  let danger = 0;
  if (t >= 11) danger = phase(t, [11, 13]);
  if (cashedOut) danger = clamp(danger - phase(t, [cashoutT, cashoutT + 1.5]), 0, 1);

  // ----- Camera shake -----
  // Source: RoundSequencer.jsx:181-188.
  let shakeAmp = 0;
  if (t >= 7) shakeAmp = phase(t, [7, 9.5]) * 0.6;
  if (t >= 9.5) shakeAmp = 0.6 + phase(t, [9.5, 13]) * 1.4;
  if (cashedOut) shakeAmp = clamp(shakeAmp - phase(t, [cashoutT, cashoutT + 0.6]) * 4, 0, 0);
  const shake = {
    x: Math.sin(t * 47) * shakeAmp + Math.sin(t * 23) * shakeAmp * 0.6,
    y: Math.cos(t * 53) * shakeAmp * 0.8 + Math.sin(t * 31) * shakeAmp * 0.5,
  };

  // ----- Dial rotation -----
  // Source: RoundSequencer.jsx:191-197.
  let dialAngle = -t * 6;
  const completedKeys = keyAngles.reduce((a, b) => a + (b >= 1 ? 1 : 0), 0);
  dialAngle += completedKeys * 8;
  if (t >= 10) dialAngle -= phase(t, [10, 14]) * 80;

  // ----- Open progress -----
  // Source: RoundSequencer.jsx:199-212.
  let openProgress = 0;
  let openMode: "win" | "bust" | undefined;
  if (cashedOut) {
    openMode = "win";
    openProgress = smooth(phase(t, [cashoutT + 0.8, cashoutT + 2.6]));
  } else if (outcome === "bust" && t >= VB_KEY5.turnEnd) {
    openMode = "bust";
    openProgress = smooth(phase(t, [14.0, 15.6]));
    // Spin dial fast during open.
    dialAngle -= phase(t, [VB_KEY5.turnEnd, 15.6]) * 200;
  }

  // ----- Time-freeze / desaturation flags -----
  // Source: RoundSequencer.jsx:215-216.
  const timeFreeze = cashedOut && phase(t, [cashoutT, cashoutT + 0.6]) < 1;
  const desaturate =
    outcome === "bust" && t >= VB_KEY5.turnEnd && t < VB_KEY5.turnEnd + 0.5;

  // ----- Cashout / bust flash windows -----
  // cashoutFlash: 0.4s window after cashout. Source: Scenes.jsx:43, 142.
  const cashoutFlash =
    cashedOut && t < cashoutT + 0.4
      ? clamp(1 - (t - cashoutT) / 0.4, 0, 1)
      : 0;
  // bustFlash: 0.3s window after 5th key turn. Source: Scenes.jsx:44, 153.
  const bustFlash =
    outcome === "bust" && t >= VB_KEY5.turnEnd && t < VB_KEY5.turnEnd + 0.3
      ? clamp(1 - (t - VB_KEY5.turnEnd) / 0.3, 0, 1)
      : 0;

  // ----- Final result banner gating -----
  // Source: RoundSequencer.jsx:220.
  let showResult: "win" | "bust" | null = null;
  if (cashedOut && t >= cashoutT + 1.0) showResult = "win";
  else if (outcome === "bust" && t >= 15.0) showResult = "bust";

  return {
    t,
    multiplier,
    cashoutMultiplier,
    cashedOut,
    keyStates,
    keyAngles,
    hasKeys,
    keySmokes,
    keyFlights,
    tension,
    danger,
    shake,
    dialAngle,
    openProgress,
    openMode,
    timeFreeze,
    desaturate,
    cashoutFlash,
    bustFlash,
    showResult,
    outcome,
    cashoutT,
  };
}
